/**
 * ★★ 写操作的超时时长与超时话术 · 真本（市民端唯一一份）★★
 *
 * ════════ 这个文件为什么必须存在（2026-09-04 事故）════════
 *
 * 当天主公在「收到的投诉」里提交答辩，界面弹「请求超时，请稍后重试」，他照做重试，
 * 一共提了三次。服务端日志里三次**全都成功**（round 1 / 2 / 3，uploaded:1 failed:0），
 * 于是本地索引里躺着三条重复答辩。
 *
 * 两个原因，缺一不可，所以两个都要在本文件里堵死：
 *
 *   ① **时长不够**。`client.ts` 的 `TIMEOUT_MS` 是全局 8 秒，而
 *      提交投诉 / 补录 / 答辩这三条路服务端要做的是：
 *        写乐享工单 → 建案卷夹 → 写案卷各节 → **逐件把附件上传乐享（三步预签名，串行）**
 *        → 写本地索引。
 *      实测（2026-09-04 01:27，**一张 373KB 的图**）：
 *        01:27:21 案卷定位 → 01:27:56 附件入夹 → 01:27:58 答辩落定，**约 37 秒**。
 *      —— 8 秒连一张小图都盖不住，等于每一次带附件的提交都注定"超时"。
 *
 *   ② **话术断言了失败**。「请求超时，请稍后重试」对**读**接口是对的
 *      （没读到就是没读到，重试无害）；对**写**接口是错的 ——
 *      连接断了只说明**前端不知道结果**，不说明服务端没干成。
 *      让一个不知道结果的人去「重试」，就是在教他造重复数据。
 *
 * ════════ 纪律 ════════
 *
 * ★ 数字与句子**只有这一份**。调用点不许自己写秒数、不许自己拼超时句子；
 *   它们最多从 `WRITE_CHECK_WHERE` 里挑一个"去哪儿核对"的名词。
 *
 * ★ 凡是新增「市民按一下就落库」的接口，**必须**在 `WRITE_CHECK_WHERE` 里登记一个去向，
 *   并在 api 函数里把 `writeCheck` 传给 `requestRaw`。漏登记的症状就是本次事故重演：
 *   界面说"失败"，其实成了，用户去重提。
 *
 * ★ **不要**把 `client.ts` 的全局 `TIMEOUT_MS` 调大来"顺手解决"这件事。
 *   读接口 8 秒是对的：列表、详情该快就得快，慢了要早点告诉人。
 *   为了写接口把读接口一起拖成半分钟，是拿全站响应感去换三个页面。
 */

/* ────────────────────────────────────────────────────────────────────────
 * 一、时长
 * ──────────────────────────────────────────────────────────────────────── */

const SECOND = 1000;
const MB = 1024 * 1024;

/**
 * 基础耗时：服务端**与附件无关**的那一段（写乐享工单、建案卷夹、写案卷各节、写本地索引）。
 *
 * 依据：2026-09-04 实测那一笔**只带一张 373KB 图**、全程约 37 秒 ——
 * 也就是说这一段本身就是**几十秒**量级，而不是几百毫秒。
 * 而且 37 秒还只是从「案卷定位」那条日志算起的，请求进服务端到那一行之前的那一截没进这个数。
 * 所以取 60 秒：在实测量级上留一倍余量，**不是**拍脑袋的整数。
 */
export const WRITE_TIMEOUT_BASE_MS = 60 * SECOND;

/**
 * 每一件附件的固定开销：乐享三步预签名里的第①步（申请上传凭证）与第③步（确认），
 * 两次 MCP 往返，外加子夹定位。**逐件串行**，所以是乘件数，不是取最大值。
 */
export const WRITE_TIMEOUT_PER_ATTACHMENT_MS = 30 * SECOND;

/**
 * 每 MB（原文字节）再给多少：对应第②步「打字节」那一次 HTTP PUT。
 *
 * ★★ 与服务端**错开**的关键就在这个数。服务端 `lexiang.service.ts` 给这一步的时限是
 *   `min(5min, 60s + ceil(MB) × 3s)`。前端给到 **8s/MB**（服务端的 2.67 倍），
 *   目的只有一个：**让服务端先说话**。
 *   前端若先断，用户拿到的是「不知道结果」；服务端先断，用户拿到的是后端那句
 *   「附件『X』上传中断…本地副本仍在」——**后一句才是有用的信息**。
 *   （同一条道理 `client.ts` 的 `CREDIT_TIMEOUT_MS` 与要素预填的超时注释里已经写过两次。）
 */
export const WRITE_TIMEOUT_PER_MB_MS = 8 * SECOND;

/**
 * 封顶 10 分钟。
 *
 * ★ 这是一个**故意的妥协**，不是"算出来的上界"，下一个人别把它当成安全线：
 *   服务端在极端入参下（20 件、合计 45MB＝请求体闸的物理上限）自己能耗到
 *   20 × (60s + 3s/MB×几 MB) ≈ 22 分钟才放弃 —— 真要「永远比服务端晚放弃」，
 *   前端得挂 25 分钟。那个数对着一个手机页面是不成立的：
 *   人不会等，网关也未必让等。
 * ★ 所以超过封顶的那一小块残余风险，是靠**话术**兜的：超时不说"失败"，
 *   而是如实说"可能已经成功，请先去核对"。两者是一套，缺一不可。
 */
export const WRITE_TIMEOUT_MAX_MS = 10 * 60 * SECOND;

/** 本模块只认这一个形状：有没有 base64 内容。名字、类型都与时长无关。 */
export interface AttachmentSizeLike {
  contentBase64?: string | undefined;
}

/**
 * base64 串长度换算回**原文**字节数。
 *
 * 只做长度换算，不解码（几十 MB 的串解一遍纯属浪费）：
 * 每 4 个字符还原 3 字节，末尾的 `=` 每个抵掉 1 字节。
 */
export function rawBytesOfBase64(b64: string): number {
  const s = String(b64 ?? '');
  const len = s.length;
  if (len === 0) return 0;
  let pad = 0;
  if (s.charCodeAt(len - 1) === 61) pad++;
  if (len > 1 && s.charCodeAt(len - 2) === 61) pad++;
  return Math.max(0, Math.floor((len * 3) / 4) - pad);
}

/**
 * 这一次提交该等多久。
 *
 *   min( 封顶, 基础 ＋ 件数 × 每件固定开销 ＋ ⌈原文MB⌉ × 每MB )
 *
 * 入参就是**要发出去的那个附件数组本身** —— 调用点不必也不许自己算体积、传秒数。
 * 传 `undefined` / 空数组（没有附件）时就是基础值。
 */
export function writeTimeoutMs(attachments?: readonly AttachmentSizeLike[] | undefined): number {
  const list = attachments ?? [];
  let rawBytes = 0;
  for (const a of list) rawBytes += rawBytesOfBase64(a?.contentBase64 ?? '');
  const wanted =
    WRITE_TIMEOUT_BASE_MS +
    list.length * WRITE_TIMEOUT_PER_ATTACHMENT_MS +
    Math.ceil(rawBytes / MB) * WRITE_TIMEOUT_PER_MB_MS;
  return Math.min(WRITE_TIMEOUT_MAX_MS, wanted);
}

/* ────────────────────────────────────────────────────────────────────────
 * 二、话术
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * 「去哪儿核对」的登记表。**新增写接口必须在这里加一条**，见文件头纪律。
 *
 * 只登记名词（去处），句子由 `writeTimeoutText()` 一处拼 ——
 * 每个调用点各拼一句，早晚会拼出三种口径。
 */
export const WRITE_CHECK_WHERE = {
  /** 提交投诉 → 我的投诉列表 */
  complaint: '「我的投诉」',
  /** 信息补录 → 该案详情页（补录记录在那里逐轮列着） */
  amend: '「我的投诉」里这件投诉的详情页',
  /** 被投诉方答辩 → 「收到的投诉」里该案详情页 */
  defense: '「收到的投诉」里这件投诉的详情页',
} as const;

export type WriteCheckWhere = (typeof WRITE_CHECK_WHERE)[keyof typeof WRITE_CHECK_WHERE];

/**
 * 写操作超时时给市民看的那句话。
 *
 * ★★ **一个「失败」字样都不许出现**，这是本文件存在的一半理由。
 *   前端断线只证明"没等到回话"，证明不了"没办成"。断言失败，就是在制造重复提交。
 * ★ 句子必须**可照做**：说清楚 ①可能已经成功 ②先去哪儿看 ③看到了就别再提交。
 *   只说"结果未知"而不给下一步，等于把人推去重试 —— 和说"失败"是一个后果。
 */
export function writeTimeoutText(where: WriteCheckWhere): string {
  return (
    `网络较慢，这一笔可能已经提交成功。请先到${where}里查看，` +
    `确认没有再重新提交，避免重复。`
  );
}

/**
 * 写超时那一刻弹窗的**标题**。
 *
 * ★ 三个页面原先一律 `title: '提交失败'` / `'补录失败'`。标题写"失败"、正文写
 *   "可能已经成功"，人只看得进标题 —— 等于话术白改。所以标题也有一份真源。
 */
export const WRITE_INCONCLUSIVE_TITLE = '结果待确认';

/* ────────────────────────────────────────────────────────────────────────
 * 三、判重（＝上面那次超时重试的**另一半**）
 * ────────────────────────────────────────────────────────────────────────
 *
 * 服务端（2026-09-05 批 ⑮）给答辩提交加了防重：同一案 ＋ 内容逐字节相同 ＋ 5 分钟内，
 * 判为「上一笔其实成功了，这是超时重试」，**不新建轮次**，直接回上一轮的结果，
 * 并带回 `deduped:true` 与一句中文 `note`（契约见 `@cdfjx/shared` 的
 * `respondent-contract.ts` 的 `RespondentDefenseResult`）。
 *
 * ★★ 这一条**不是错误态**，是「成功，而且已经并成同一次」。三条纪律：
 *   ① 视觉上必须与正常成功一致或更弱 —— 所以 `WriteAlert.tone` 的类型
 *      直接写死成 `'plain'`：想弹成 `Alert.tsx` 的 `fail` 那一档**编译就不过**，
 *      不靠人自觉。
 *   ② **不许再报"这是第几次提交"**。判重时 `round` 是被并进去的**上一轮**的轮次，
 *      照着它说「这是第 1 次提交」，正是本批在治的那句「看着像没提交上」——
 *      用户会再点一次，等于白治。
 *   ③ 服务端那句 `note` **原样摆出来**（差了几分钟只有它知道），
 *      前端只在后面补一句固定收尾，把「材料已经在协会手里、不必再提交」说死。
 *      note 只说了"内容相同、按同一次处理"，没说"你不用再做什么"——
 *      少这一句，人还是会犹豫要不要再传一遍。
 */

/** 一个可以直接喂给 `useAlert()` 的弹窗。`tone` 锁死 `'plain'`，见上面纪律①。 */
export interface WriteAlert {
  title: string;
  tone: 'plain';
  text: string;
}

/** 判重时的弹窗标题。**不许出现「失败」「重复」这类字眼**——他没做错任何事。 */
export const WRITE_DEDUPED_TITLE = '材料已经收到';

/** 服务端没给 `note` 时的兜底句。照样不编"几分钟前"这种只有服务端知道的细节。 */
export const WRITE_DEDUPED_FALLBACK = '这一笔与您上一次提交的内容相同，已按同一次处理。';

/** 固定收尾：把「东西在协会手里了、不必再提交」说死。见上面纪律③。 */
export const WRITE_DEDUPED_TAIL = '材料已经在协会手里，不必再提交一次。';

/** 判重那句话：服务端的解释（有就用，没有走兜底）＋ 固定收尾。 */
export function writeDedupedText(note?: string | undefined): string {
  const head = String(note ?? '').trim() || WRITE_DEDUPED_FALLBACK;
  // 服务端那句怎么收尾不由前端假设：没有句读就补一个，免得两句黏成一句。
  const sep = /[。！？.!?]$/.test(head) ? '' : '。';
  return `${head}${sep}${WRITE_DEDUPED_TAIL}`;
}

/**
 * 答辩提交成功之后弹哪一个 —— **两支都在这一处**，页面只负责把它交给 `useAlert()`。
 *
 * 放在这里而不是页面里，是因为「判重那一支长什么样」必须能被断言实跑验到：
 * 埋在 JSX 里的字符串验不了，验不了的话术迟早漂。
 */
export function defenseSubmittedAlert(o: {
  round: number;
  deduped: boolean;
  note: string;
}): WriteAlert {
  if (o.deduped) {
    return { title: WRITE_DEDUPED_TITLE, tone: 'plain', text: writeDedupedText(o.note) };
  }
  return {
    title: '答辩材料已提交',
    tone: 'plain',
    // `round` 拿不到时（为 0）**就不说第几次**，不编一个次数出来
    text: (o.round > 0 ? `这是第 ${o.round} 次提交。` : '') + '协会工作人员会看到您提交的内容。',
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * 四、等待期的交代
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * 提交按钮在等待期的字样。
 *
 * 原先三处都写「提交中…」。一张手机拍的图要走几十秒，「提交中…」配一个转圈，
 * 在第二十秒上看起来就是"卡死了"——而这一屏正是最不能让人乱按、乱退的一屏。
 */
export const WRITE_BUSY_BUTTON = '正在上传，请稍候…';

/** 遮罩层的主句。 */
export const WRITE_BUSY_TITLE = '正在上传材料…';

/** 遮罩层的副句：把「要多久」和「别做什么」一次说完。 */
export const WRITE_BUSY_HINT = '可能需要几十秒，请不要关闭页面';
